/**
 * Composable for managing modal triggers with focus restoration
 *
 * Usage:
 * const { triggerElement, openModal } = useModalTrigger();
 *
 * // In template:
 * <button @click="openModal">Open Modal</button>
 * <Modal :showing="showModal" :trigger-element="triggerElement" @close="showModal = false">
 */

import { ref } from 'vue';

export const useModalTrigger = () => {
  const triggerElement = ref(null);

  /**
   * Call this function when opening a modal to capture the trigger element
   * @param {Event} event - The click event (optional)
   */
  const openModal = (event = null) => {
    // If event is provided, use the target element
    if (event && event.target) {
      triggerElement.value = event.target;
    } else {
      // Otherwise use the currently focused element
      triggerElement.value = document.activeElement;
    }
  };

  /**
   * Call this function with a specific element to set as trigger
   * @param {HTMLElement} element - The element to use as trigger
   */
  const setTriggerElement = (element) => {
    triggerElement.value = element;
  };

  /**
   * Clear the trigger element
   */
  const clearTriggerElement = () => {
    triggerElement.value = null;
  };

  return {
    triggerElement,
    openModal,
    setTriggerElement,
    clearTriggerElement,
  };
};
